/* ==========================================================================
   Smoosh — Cart drawer upsell ADD handler
   - Single click delegation on the cart drawer; works for both featured
     cards and list rows (anything with [data-smoosh-upsell]).
   - For multi-variant products, redirects to the PDP (we can't pick a
     variant blind). For single-variant, posts to /cart/add.js and asks
     the cart-drawer to re-render so the upsell zone refreshes.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__smooshCartUpsellLoaded) return;
  window.__smooshCartUpsellLoaded = true;

  const CART_ADD_URL = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function addVariantToCart(variantId, qty, properties) {
    const drawer = getCartDrawer();
    const fd = new FormData();
    fd.append('id', String(variantId));
    fd.append('quantity', String(qty || 1));
    /* Underscore-prefixed line item properties stay hidden from the
       customer-facing cart/checkout UI but flow through to Shopify Admin,
       Flow, and CSV exports — the standard pattern for upsell attribution. */
    if (properties && typeof properties === 'object') {
      Object.keys(properties).forEach(function (key) {
        if (properties[key] != null) {
          fd.append('properties[' + key + ']', String(properties[key]));
        }
      });
    }
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      fd.append('sections', drawer.getSectionsToRender().map(s => s.id).join(','));
      fd.append('sections_url', window.location.pathname);
    }
    return fetch(CART_ADD_URL, {
      method: 'POST',
      body: fd,
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' }
    })
      .then(r => r.json())
      .then(response => {
        if (response.status) {
          console.warn('[smoosh-upsell] add error', response);
          return response;
        }
        // Re-render the cart drawer so the upsell list / featured cards refresh.
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.classList.remove('is-empty');
          drawer.renderContents(response);
        }
        // Publish theme's cartUpdate event so other listeners (Junip,
        // upsell apps, the SBR section's compositor, etc.) refresh too.
        try {
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'smoosh-cart-upsell',
              productVariantId: String(variantId),
              cartData: response
            });
          }
        } catch (e) { /* non-fatal */ }
        return response;
      })
      .catch(err => console.warn('[smoosh-upsell] add failed', err));
  }

  /* Show the inline picker (swap card body → swatches) for a multi-variant card. */
  function openPicker(card) {
    const body   = card.querySelector('[data-smoosh-upsell-body]');
    const picker = card.querySelector('.smoosh-cart-upsell__picker');
    if (!body || !picker) return false;
    body.hidden = true;
    picker.hidden = false;
    card.classList.add('is-picking');
    return true;
  }

  function closePicker(card) {
    const body   = card.querySelector('[data-smoosh-upsell-body]');
    const picker = card.querySelector('.smoosh-cart-upsell__picker');
    if (!body || !picker) return;
    picker.hidden = true;
    body.hidden = false;
    card.classList.remove('is-picking');
  }

  /* Persistent on-card swatch click — updates the card's selected
     variant without firing ADD. The ADD button below reads
     data-variant-id at click time, so a single source of truth. */
  function selectSwatch(card, swatch) {
    if (!card || !swatch || swatch.disabled) return;
    const variantId = swatch.getAttribute('data-variant-id');
    const variantLabel = swatch.getAttribute('data-variant-label') || '';
    const variantImg = swatch.getAttribute('data-variant-img');
    if (!variantId) return;

    // Update the card's selected variant (ADD reads this).
    card.setAttribute('data-variant-id', variantId);

    // Mark the active swatch.
    card.querySelectorAll('[data-smoosh-upsell-pick]').forEach(s => {
      const on = s === swatch;
      s.classList.toggle('is-on', on);
      s.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    // Variant name label (e.g. "SAGE").
    const label = card.querySelector('[data-smoosh-upsell-variant-label]');
    if (label) label.textContent = variantLabel.toUpperCase();

    // Swap the card image if the variant has its own.
    if (variantImg) {
      const img = card.querySelector('[data-smoosh-upsell-img]');
      if (img) {
        img.src = variantImg;
        img.removeAttribute('srcset');
      }
    }
  }

  function onClick(e) {
    // Cancel picker (legacy popover — only used for non-featured contexts)
    if (e.target.closest('[data-smoosh-upsell-picker-close]')) {
      e.preventDefault();
      const card = e.target.closest('[data-smoosh-upsell]');
      if (card) closePicker(card);
      return;
    }

    // Swatch click → just update the card's selected variant (don't add yet).
    const swatch = e.target.closest('[data-smoosh-upsell-pick]');
    if (swatch && !swatch.disabled) {
      e.preventDefault();
      const card = swatch.closest('[data-smoosh-upsell]');
      selectSwatch(card, swatch);
      return;
    }

    // ADD button → add the card's currently-selected variant.
    const btn = e.target.closest('.smoosh-cart-upsell__add');
    if (!btn) return;
    const card = btn.closest('[data-smoosh-upsell]');
    if (!card) return;
    e.preventDefault();

    const variantId = card.getAttribute('data-variant-id');
    if (!variantId) return;

    /* Cart-drawer upsell attribution. Walk up to find which zone owns the
       click (featured Sidekicks vs Soap Stock Up list), then index the card
       within that zone's siblings. Properties land on the cart line item +
       order — Shopify Flow reads them to apply an order tag for analytics. */
    var featuredZone = card.closest('.smoosh-cart-upsell__featured');
    var listZone     = card.closest('.smoosh-cart-upsell__list');
    var zone         = featuredZone || listZone;
    var placement    = featuredZone ? 'smoosh_sidekicks'
                       : listZone   ? 'soap_stock_up'
                       : null;
    var properties   = null;
    if (zone && placement) {
      var siblings = zone.querySelectorAll('[data-smoosh-upsell]');
      var position = Array.prototype.indexOf.call(siblings, card) + 1;
      properties = {
        _source: 'cart_drawer',
        _placement: placement,
        _upsell_position: String(position)
      };
    }

    btn.classList.add('is-loading');
    btn.disabled = true;
    addVariantToCart(variantId, 1, properties).finally(() => {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    });
  }

  /* Delegate at document level so it survives cart-drawer re-renders. */
  document.addEventListener('click', onClick);
})();
